import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { SaveStatus, type SaveState } from '@/components/shared/SaveStatus';
import type { MetricsGates, MetricsStage } from '@/services/adminMetricsService';
import { cn } from '@/lib/utils';

const TREND_ICON: Record<string, string> = {
  down: '↓',
  up: '↑',
  flat: '→',
  insufficient_data: '?',
};

const TREND_CLASS: Record<string, string> = {
  down: 'text-success',
  up: 'text-destructive',
  flat: 'text-muted-foreground',
  insufficient_data: 'text-muted-foreground',
};

function stageLabel(stage: MetricsStage): string {
  if (stage === 'pre_s0') return 'pré-S0';
  return stage;
}

function stageBadgeVariant(stage: MetricsStage) {
  if (stage === 'S2') return 'success' as const;
  if (stage === 'S1') return 'secondary' as const;
  if (stage === 'S0') return 'default' as const;
  return 'outline' as const;
}

interface GatesPanelProps {
  data: MetricsGates;
  /** Called when the founder saves a new CAC estimate (value in euros). */
  onSaveCac: (euros: number) => void;
  /** F103/A25 — explicit save feedback for the CAC input (no silent failure). */
  cacSaveStatus?: SaveState;
  cacSaveErrorMessage?: string;
  onRetryCac?: () => void;
}

/**
 * §24 validation gates panel.
 * Includes a CAC manual input (euros) that fires onSaveCac on blur/Enter.
 */
export function GatesPanel({
  data,
  onSaveCac,
  cacSaveStatus = 'idle',
  cacSaveErrorMessage,
  onRetryCac,
}: GatesPanelProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  function handleSave() {
    const raw = inputRef.current?.value ?? '';
    const euros = parseFloat(raw);
    if (!isNaN(euros) && euros > 0) {
      onSaveCac(euros);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') handleSave();
  }

  const cacEuros = data.cac_cents != null ? Math.round(data.cac_cents / 100) : null;

  return (
    <div className="space-y-5">
      {/* Stage badge */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">
          {t('admin.metrics.gates.stage_label')}
        </span>
        <Badge data-testid="gates-stage-badge" variant={stageBadgeVariant(data.stage)}>
          {stageLabel(data.stage)}
        </Badge>
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm sm:grid-cols-4 [&>div]:min-w-0">
        {/* Cold paying */}
        <div>
          <dt className="text-muted-foreground">{t('admin.metrics.gates.cold_paying')}</dt>
          <dd data-testid="gates-cold-paying" className="mt-1 text-xl font-bold tabular-nums">
            {data.cold_paying_count}
          </dd>
        </div>

        {/* Churn trend */}
        <div>
          <dt className="text-muted-foreground">{t('admin.metrics.gates.churn_trend')}</dt>
          <dd
            className={cn(
              'mt-1 text-xl font-bold',
              TREND_CLASS[data.cohort_churn.trend] ?? 'text-foreground'
            )}
          >
            {TREND_ICON[data.cohort_churn.trend] ?? '?'}{' '}
            <span className="text-sm font-normal">
              {t(`admin.metrics.gates.trend_${data.cohort_churn.trend}`)}
            </span>
          </dd>
        </div>

        {/* CAC input */}
        <div>
          <dt className="text-muted-foreground" id="cac-label">
            {t('admin.metrics.cac_manual')}
          </dt>
          <dd className="mt-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-sm text-muted-foreground">€</span>
              <Input
                ref={inputRef}
                type="number"
                min={0}
                step={1}
                defaultValue={cacEuros ?? ''}
                aria-labelledby="cac-label"
                className="h-8 w-24 text-sm tabular-nums"
                disabled={cacSaveStatus === 'saving'}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                data-testid="cac-input"
              />
              <SaveStatus
                status={cacSaveStatus}
                errorMessage={cacSaveErrorMessage}
                onRetry={onRetryCac}
              />
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t('admin.metrics.gates.cac_hint')}
            </p>
          </dd>
        </div>

        {/* Payback */}
        <div>
          <dt className="text-muted-foreground">{t('admin.metrics.gates.payback')}</dt>
          <dd data-testid="gates-payback" className="mt-1 text-xl font-bold tabular-nums">
            {data.payback_months != null ? (
              <>
                {data.payback_months}
                <span className="ml-1 text-sm font-normal text-muted-foreground">
                  {t('admin.metrics.gates.months_suffix')}
                </span>
              </>
            ) : (
              <span className="text-base font-normal text-muted-foreground">—</span>
            )}
          </dd>
        </div>
      </dl>
    </div>
  );
}
